/**
 * Scatter chart. Plots `encoding.xKey` vs `encoding.yKey` as markers. When the
 * pairs are numeric it adds an OLS trend line and a 1:1 reference line.
 * An optional `series[0].errorKey` adds symmetric vertical error bars.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { colorAt, SEMANTIC } from '../theme/palette';
import { linearRegression } from '../shared/regression';
import { identityLineTrace } from '../shared/references';
import { makeErrorY } from '../shared/traces';
import { xColumn } from '../shared/encoding';
import { toFiniteNumber } from '../shared/number';
import { uniqueValues } from '../shared/uniqueValues';
import { figureScale } from '../theme/scale';
import { DEFAULT_MARKER_SIZE, SERIES_LINE_WIDTH } from '../theme/layout';

export const scatterChart: ChartTypeDefinition = {
  id: 'scatter',
  label: 'Scatter',
  capabilities: {
    multiSeries: false,
    supportsXKey: true,
    supportsYKey: true,
    supportsGroupBy: true,
    supportsLimits: true,
    filterable: true,
    supportsStacked: false,
    legendByDefault: true,
  },
  buildTraces(model, ctx) {
    const { data, encoding, series, groupColors, options } = model;
    const yKey = encoding.yKey ?? 'y';
    const groupKey = encoding.groupKey;
    const errorKey = series[0]?.errorKey;
    // Marks scale with the figure like the type does (see ../theme/scale): held
    // absolute, an 8px dot is proportionally 38% smaller on the 1000px report
    // figure than on the card it was tuned against.
    const scale = figureScale(ctx.figureWidth);
    const markerSize = (options?.markerSize ?? DEFAULT_MARKER_SIZE) * scale;

    // Resolve the x channel once; absent xKey falls back to the row index.
    const xValues = xColumn(data, encoding.xKey);

    // Keep row indices so an error column stays aligned with plotted points.
    // A blank/null cell is not a numeric point — excluded, never coerced to 0.
    const numericIdx = data
      .map((_, i) => i)
      .filter((i) => toFiniteNumber(xValues[i]) !== null && toFiniteNumber(data[i][yKey]) !== null);
    const useNumeric = numericIdx.length > 0;

    const traces: ChartTrace[] = [];

    if (groupKey) {
      // One marker trace per category (color by groupColors[cat] || palette),
      // mirroring spatialCategorical/box. Only numeric points are plotted so the
      // markers stay aligned with the shared trend/1:1 lines below.
      const categories = uniqueValues(data, groupKey);
      categories.forEach((cat, i) => {
        const idx = numericIdx.filter((j) => String(data[j][groupKey]) === cat);
        traces.push({
          x: idx.map((j) => toFiniteNumber(xValues[j])),
          y: idx.map((j) => toFiniteNumber(data[j][yKey])),
          mode: 'markers',
          type: 'scatter',
          name: cat,
          marker: { color: groupColors[cat] || colorAt(i, ctx.palette), size: markerSize },
          ...makeErrorY(
            idx.map((j) => data[j]),
            errorKey
          ),
        });
      });
    } else {
      const sourceRows = useNumeric ? numericIdx.map((i) => data[i]) : data;
      const traceX = useNumeric ? numericIdx.map((i) => toFiniteNumber(xValues[i])) : xValues;
      const traceY = useNumeric
        ? numericIdx.map((i) => toFiniteNumber(data[i][yKey]))
        : data.map((d) => d[yKey]);
      traces.push({
        x: traceX,
        y: traceY,
        mode: 'markers',
        type: 'scatter',
        marker: { color: colorAt(0, ctx.palette), size: markerSize },
        name: `${yKey} vs ${encoding.xKey ?? 'index'}`,
        ...makeErrorY(sourceRows, errorKey),
      });
    }

    // Trend (OLS) and 1:1 line are computed over ALL numeric points, independent
    // of grouping, so they stay meaningful whether or not the points are split.
    if (useNumeric) {
      const xs = numericIdx.map((i) => toFiniteNumber(xValues[i]) as number);
      const ys = numericIdx.map((i) => toFiniteNumber(data[i][yKey]) as number);
      const min = Math.min(...xs);
      const max = Math.max(...xs);
      const reg = linearRegression(xs, ys);
      if (reg) {
        traces.push({
          x: [min, max],
          y: [reg.slope * min + reg.intercept, reg.slope * max + reg.intercept],
          mode: 'lines',
          type: 'scatter',
          name: 'Trend',
          // Reference lines (Trend + 1:1 below) share one legend toggle: shown
          // only when `showReferenceLine` is on, drawn regardless.
          showlegend: !!options?.showReferenceLine,
          line: { dash: 'dashdot', width: SERIES_LINE_WIDTH * scale, color: SEMANTIC.trend },
        });
      }
      traces.push({
        ...identityLineTrace(min, max, SEMANTIC.identity, scale),
        showlegend: !!options?.showReferenceLine,
      });
    }

    return traces;
  },
  // xy clouds and 1:1 plots read better point-by-point than with the 'x unified'
  // default, where many points share an x and the tooltip stacks unreadably.
  buildLayout() {
    return { hovermode: 'closest' };
  },
};

registerChart(scatterChart);
